import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Activity, RefreshCw, Scale, LogIn } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid } from 'recharts';
import SiteFooter from '../components/SiteFooter';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/demo`;
const LABEL = 'DEMO MODE — SYNTHETIC DATA — NOT LIVE MARKET QUOTES — NOT EXECUTABLE';
const num = (v, d = 0) => { const n = parseFloat(v); return Number.isFinite(n) ? n : d; };
const money = (v) => (typeof v === 'number' ? `${v < 0 ? '-' : ''}$${Math.abs(v).toFixed(2)}` : v ?? '—');

// Module-level so React keeps the same element across renders (no focus loss while typing)
const NumField = ({ label, value, onChange, step = 'any', min, testid }) => (
  <label className="block">
    <span className="text-[11px] font-mono uppercase tracking-wider text-slate-400">{label}</span>
    <input
      type="number" inputMode="decimal" step={step} min={min} value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full h-10 rounded-md bg-slate-950 border border-slate-700 px-3 text-sm font-mono text-slate-100 focus:outline-none focus:border-amber-500"
      data-testid={testid}
    />
  </label>
);

const Select = ({ label, value, onChange, options, testid }) => (
  <label className="block">
    <span className="text-[11px] font-mono uppercase tracking-wider text-slate-400">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full h-10 rounded-md bg-slate-950 border border-slate-700 px-2 text-sm text-slate-100 focus:outline-none focus:border-amber-500" data-testid={testid}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </label>
);

const Stat = ({ label, value, tone = 'text-slate-100', testid }) => (
  <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800">
    <p className="text-[11px] font-mono uppercase tracking-wider text-slate-400">{label}</p>
    <p className={`text-lg font-semibold font-mono ${tone}`} data-testid={testid}>{value}</p>
  </div>
);

const Assumptions = ({ a }) => a ? (
  <p className="text-xs text-slate-500 mt-4 leading-relaxed" data-testid="demo-assumptions">
    Assumptions: {a.pricing_model}. IV {(a.implied_volatility * 100).toFixed(0)}% · risk-free {(a.risk_free_rate * 100).toFixed(2)}% · dividend yield {(a.dividend_yield * 100).toFixed(2)}% · valuation date {a.valuation_date} · multiplier {a.contract_multiplier}{a.synthetic_spread_pct != null ? ` · synthetic spread ${(a.synthetic_spread_pct * 100).toFixed(0)}%` : ''}.
  </p>
) : null;

const useDemo = (path, body) => {
  const [data, setData] = useState(null); const [error, setError] = useState(null); const [loading, setLoading] = useState(false);
  const key = JSON.stringify(body);
  useEffect(() => {
    let live = true; setLoading(true);
    const t = setTimeout(() => {
      axios.post(`${API}/${path}`, body).then((r) => { if (live) { setData(r.data); setError(null); } })
        .catch((e) => { if (live) setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Inputs are outside the supported demo range.'); })
        .finally(() => live && setLoading(false));
    }, 350);
    return () => { live = false; clearTimeout(t); };
  }, [path, key]);
  return { data, error, loading };
};

// Demo → real tool. Guests go to login; members go to the tool (the backend still enforces the subscription).
const DemoCta = ({ label, tab, testid }) => {
  const { isAuthenticated } = useAuth();
  const { isPremium, plans } = useSubscription();
  const monthly = plans?.find((p) => p.plan_type === 'monthly');
  const to = isAuthenticated ? `/?tab=${tab}` : '/login';
  const note = isPremium
    ? 'Opens the live tool with delayed market quotes.'
    : `${isAuthenticated ? 'Your account is on the free tier. ' : 'Sign in or create a free account first. '}The live tool requires Option Wizard Premium${monthly ? ` ($${monthly.price.toFixed(2)}/month${monthly.trial_days > 0 ? `, ${monthly.trial_days}-day trial` : ''})` : ''}; registration alone does not unlock it.`;
  return (
    <div className="mt-6 pt-5 border-t border-slate-800 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid={`${testid}-cta`}>
      <p className="text-xs text-slate-400 max-w-xl">{note}</p>
      <Link to={to} className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold font-mono text-xs uppercase tracking-wider transition-colors flex-shrink-0" data-testid={`${testid}-cta-btn`}>
        {label} <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
};

const Panel = ({ icon: Icon, title, blurb, children, cta }) => (
  <section className="rounded-xl border border-slate-800/80 bg-slate-900/70 backdrop-blur-md shadow-xl p-5 sm:p-7">
    <div className="flex items-center gap-3 mb-2"><div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20"><Icon className="h-5 w-5 text-amber-400" /></div><h2 className="text-xl sm:text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h2></div>
    <p className="text-sm text-slate-400 mb-6">{blurb}</p>
    {children}
    {cta}
  </section>
);

const Err = ({ error }) => error ? <p className="text-sm text-rose-300 mt-3" data-testid="demo-error">{error}</p> : null;

const PositionDemo = () => {
  const [f, setF] = useState({ S: '100', dte: '30', iv: '30', type1: 'call', side1: 'short', k1: '105', p1: '2.10', type2: 'call', side2: 'long', k2: '110', p2: '0.80', legs: '2' });
  const set = (k) => (v) => setF((o) => ({ ...o, [k]: v }));
  const legs = [{ option_type: f.type1, position_type: f.side1, strike: num(f.k1, 1), quantity: 1, premium: num(f.p1) }];
  if (f.legs === '2') legs.push({ option_type: f.type2, position_type: f.side2, strike: num(f.k2, 1), quantity: 1, premium: num(f.p2) });
  const { data, error } = useDemo('position', { underlying_price: num(f.S, 1), legs, days_to_expiration: Math.round(num(f.dte)), implied_volatility: num(f.iv, 30) / 100 });
  const m = data?.metrics;
  return (
    <Panel icon={Activity} title="Position Analyzer" cta={<DemoCta label="Explore Position Analyzer" tab="manual" testid="pa" />} blurb="Change the synthetic position and watch the expiration profit/loss, breakevens, max profit/loss and model-estimated Greeks update. Legs are valued with Black-Scholes, not market quotes.">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-3">
        <NumField label="Underlying" value={f.S} onChange={set('S')} testid="pa-underlying" />
        <NumField label="Days to Exp" value={f.dte} onChange={set('dte')} step="1" min="0" testid="pa-dte" />
        <NumField label="IV %" value={f.iv} onChange={set('iv')} testid="pa-iv" />
        <Select label="Legs" value={f.legs} onChange={set('legs')} options={[['1', '1 leg'], ['2', '2 legs']]} testid="pa-legs" />
      </div>
      {[1, 2].filter((i) => i === 1 || f.legs === '2').map((i) => (
        <div key={i} className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          <Select label={`Leg ${i} type`} value={f[`type${i}`]} onChange={set(`type${i}`)} options={[['call', 'Call'], ['put', 'Put']]} testid={`pa-type-${i}`} />
          <Select label="Side" value={f[`side${i}`]} onChange={set(`side${i}`)} options={[['long', 'Long (buy)'], ['short', 'Short (sell)']]} testid={`pa-side-${i}`} />
          <NumField label="Strike" value={f[`k${i}`]} onChange={set(`k${i}`)} testid={`pa-strike-${i}`} />
          <NumField label="Entry premium" value={f[`p${i}`]} onChange={set(`p${i}`)} testid={`pa-premium-${i}`} />
        </div>
      ))}
      <Err error={error} />
      {data && (
        <div className="mt-4" data-testid="pa-results">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat label="Max profit" value={money(m.max_profit)} tone="text-emerald-400" testid="pa-max-profit" />
            <Stat label="Max loss" value={money(m.max_loss)} tone="text-rose-400" testid="pa-max-loss" />
            <Stat label="Breakeven(s)" value={m.breakevens.length ? m.breakevens.map((b) => `$${b}`).join(' / ') : '—'} testid="pa-breakevens" />
            <Stat label="Prob. of profit (model)" value={m.probability_of_profit == null ? '—' : `${m.probability_of_profit}%`} testid="pa-pop" />
          </div>
          <div className="h-56 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.payoff_curve} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="rgba(148,163,184,0.12)" />
                <XAxis dataKey="price" tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={(v) => `$${v}`} width={64} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', fontSize: 12 }} formatter={(v) => [money(v), 'P/L at expiration']} labelFormatter={(l) => `Underlying $${l}`} />
                <ReferenceLine y={0} stroke="#64748b" />
                <ReferenceLine x={data.underlying_price} stroke="#f59e0b" strokeDasharray="4 4" />
                <Line type="monotone" dataKey="pl" stroke="#f59e0b" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
            {['delta', 'gamma', 'theta', 'vega'].map((g) => <Stat key={g} label={`Net ${g} (model)`} value={data.net_greeks[g]} testid={`pa-greek-${g}`} />)}
          </div>
          <Assumptions a={data.assumptions} />
        </div>
      )}
    </Panel>
  );
};

const RollmasterDemo = () => {
  const [f, setF] = useState({ S: '100', dte: '7', iv: '35', type: 'call', side: 'short', k: '97.5', p: '1.50', goal: 'avoid_assignment', risk: 'moderate' });
  const set = (k) => (v) => setF((o) => ({ ...o, [k]: v }));
  const { data, error } = useDemo('rollmaster', { underlying_price: num(f.S, 1), legs: [{ option_type: f.type, position_type: f.side, strike: num(f.k, 1), quantity: 1, premium: num(f.p) }], days_to_expiration: Math.max(1, Math.round(num(f.dte, 1))), implied_volatility: num(f.iv, 30) / 100, goal: f.goal, risk_tolerance: f.risk });
  return (
    <Panel icon={RefreshCw} title="Rollmaster" cta={<DemoCta label="Explore Rollmaster" tab="rollmaster" testid="rm" />} blurb="The real Rollmaster engine runs on a synthetic option chain (Black-Scholes mid ± a synthetic spread). Sells are priced at the synthetic bid and buys at the synthetic ask; none of these are executable quotes.">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-3">
        <NumField label="Underlying" value={f.S} onChange={set('S')} testid="rm-underlying" />
        <NumField label="Days to Exp" value={f.dte} onChange={set('dte')} step="1" min="1" testid="rm-dte" />
        <NumField label="IV %" value={f.iv} onChange={set('iv')} testid="rm-iv" />
        <Select label="Goal" value={f.goal} onChange={set('goal')} options={[['improve', 'Improve position'], ['avoid_assignment', 'Avoid assignment'], ['cut_loss', 'Cut losses'], ['take_profit', 'Lock in profit']]} testid="rm-goal" />
        <Select label="Risk tolerance" value={f.risk} onChange={set('risk')} options={[['conservative', 'Conservative'], ['moderate', 'Moderate'], ['aggressive', 'Aggressive']]} testid="rm-risk" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <Select label="Type" value={f.type} onChange={set('type')} options={[['call', 'Call'], ['put', 'Put']]} testid="rm-type" />
        <Select label="Side" value={f.side} onChange={set('side')} options={[['short', 'Short (sell)'], ['long', 'Long (buy)']]} testid="rm-side" />
        <NumField label="Strike" value={f.k} onChange={set('k')} testid="rm-strike" />
        <NumField label="Entry premium" value={f.p} onChange={set('p')} testid="rm-premium" />
      </div>
      <Err error={error} />
      {data && (
        <div className="mt-4" data-testid="rm-results">
          {data.candidate_status !== 'ok' ? (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200" data-testid="rm-no-candidates">
              {data.candidate_status === 'insufficient_data' ? <>Synthetic quotes are missing for this position: <ul className="list-disc list-inside mt-1">{data.data_issues.map((i) => <li key={i}>{i}</li>)}</ul></> : `No roll candidate met the current criteria (${data.candidates_evaluated} evaluated).`}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {data.roll_strategies.map((c, i) => (
                <div key={i} className="p-4 rounded-xl bg-slate-900/80 border border-slate-800" data-testid={`rm-strategy-${i + 1}`}>
                  <p className="text-[11px] font-mono uppercase tracking-wider text-amber-400">#{i + 1} · {c.risk_level} risk</p>
                  <h3 className="text-base font-semibold text-white mt-1">{c.name}</h3>
                  <p className={`text-xl font-mono font-semibold mt-2 ${c.net_credit_debit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} data-testid={`rm-net-${i + 1}`}>{c.net_credit_debit >= 0 ? 'Credit' : 'Debit'} {money(Math.abs(c.net_credit_debit))}</p>
                  <ul className="text-xs text-slate-300 font-mono mt-2 space-y-1">
                    {c.new_legs.map((l, j) => <li key={j}>{l.position_type.toUpperCase()} ${l.strike} {l.option_type.toUpperCase()} @ {l.premium} <span className="text-slate-500">(synthetic {l.position_type === 'short' ? 'bid' : 'ask'})</span></li>)}
                  </ul>
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs mt-3 text-slate-400">
                    <dt>New expiry</dt><dd className="text-right font-mono text-slate-200">{c.new_expiration}</dd>
                    <dt>Max profit</dt><dd className="text-right font-mono text-slate-200">{money(c.max_profit)}</dd>
                    <dt>Max loss</dt><dd className="text-right font-mono text-slate-200">{money(c.max_loss)}</dd>
                    <dt>Breakeven</dt><dd className="text-right font-mono text-slate-200">{typeof c.new_breakeven === 'number' ? `$${c.new_breakeven}` : c.new_breakeven}</dd>
                    <dt>POP (model)</dt><dd className="text-right font-mono text-slate-200">{c.probability_success == null ? '—' : `${c.probability_success}%`}</dd>
                  </dl>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-slate-500 mt-3">{data.pricing_basis}. {data.candidates_evaluated} candidates evaluated.</p>
          <Assumptions a={data.assumptions} />
        </div>
      )}
    </Panel>
  );
};

const RollCompareDemo = () => {
  const [f, setF] = useState({ S: '100', curK: '100', curP: '2.00', curClose: '3.40', curDte: '5', newK: '95', newP: '3.90', newDte: '35', iv: '30', type: 'put' });
  const set = (k) => (v) => setF((o) => ({ ...o, [k]: v }));
  const { data, error } = useDemo('roll-compare', {
    underlying_price: num(f.S, 1), implied_volatility: num(f.iv, 30) / 100,
    current_legs: [{ option_type: f.type, position_type: 'short', strike: num(f.curK, 1), quantity: 1, premium: num(f.curP) }],
    current_close_premiums: [num(f.curClose)],
    new_legs: [{ option_type: f.type, position_type: 'short', strike: num(f.newK, 1), quantity: 1, premium: num(f.newP) }],
    current_days_to_expiration: Math.round(num(f.curDte)), new_days_to_expiration: Math.max(1, Math.round(num(f.newDte, 1))),
  });
  const cols = data && [['Current (if closed now)', data.current_position, 'cur'], ['New position', data.new_position, 'new']];
  return (
    <Panel icon={Scale} title="Roll Comparison Calculator" cta={<DemoCta label="Compare More Strategies" tab="rollcalc" testid="rc" />} blurb="Compare closing a short option today against opening a new one at a different strike and expiration. Premiums are illustrative values you enter; metrics come from the same calculation functions as the premium tool.">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
        <NumField label="Underlying" value={f.S} onChange={set('S')} testid="rc-underlying" />
        <Select label="Option type" value={f.type} onChange={set('type')} options={[['put', 'Short put'], ['call', 'Short call']]} testid="rc-type" />
        <NumField label="IV % (for POP)" value={f.iv} onChange={set('iv')} testid="rc-iv" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <NumField label="Current strike" value={f.curK} onChange={set('curK')} testid="rc-cur-strike" />
        <NumField label="Original credit" value={f.curP} onChange={set('curP')} testid="rc-cur-premium" />
        <NumField label="Cost to close now" value={f.curClose} onChange={set('curClose')} testid="rc-cur-close" />
        <NumField label="Current DTE" value={f.curDte} onChange={set('curDte')} step="1" min="0" testid="rc-cur-dte" />
        <NumField label="New strike" value={f.newK} onChange={set('newK')} testid="rc-new-strike" />
        <NumField label="New credit" value={f.newP} onChange={set('newP')} testid="rc-new-premium" />
        <NumField label="New DTE" value={f.newDte} onChange={set('newDte')} step="1" min="1" testid="rc-new-dte" />
      </div>
      <Err error={error} />
      {data && (
        <div className="mt-4" data-testid="rc-results">
          <div className={`p-4 rounded-xl border ${data.roll.is_credit ? 'border-emerald-500/40 bg-emerald-500/10' : 'border-rose-500/40 bg-rose-500/10'}`}>
            <p className="text-[11px] font-mono uppercase tracking-wider text-slate-300">Net roll {data.roll.is_credit ? 'credit' : 'debit'} · +{data.roll.added_days} days</p>
            <p className={`text-2xl font-mono font-semibold ${data.roll.is_credit ? 'text-emerald-400' : 'text-rose-400'}`} data-testid="rc-net">{money(Math.abs(data.roll.net_credit_debit))}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {cols.map(([title, p, id]) => (
              <div key={id} className="p-4 rounded-xl bg-slate-900/80 border border-slate-800" data-testid={`rc-${id}`}>
                <h3 className="text-sm font-semibold text-white mb-2">{title}</h3>
                <dl className="grid grid-cols-2 gap-y-1 text-xs text-slate-400">
                  {id === 'cur' && <><dt>Realized P/L if closed</dt><dd className={`text-right font-mono ${p.realized_pl_if_closed >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{money(p.realized_pl_if_closed)}</dd></>}
                  <dt>Max profit</dt><dd className="text-right font-mono text-slate-200">{money(p.max_profit)}</dd>
                  <dt>Max loss</dt><dd className="text-right font-mono text-slate-200">{money(p.max_loss)}</dd>
                  <dt>Breakeven</dt><dd className="text-right font-mono text-slate-200">{p.breakevens.map((b) => `$${b}`).join(' / ') || '—'}</dd>
                  <dt>POP (model)</dt><dd className="text-right font-mono text-slate-200">{p.probability_of_profit == null ? '—' : `${p.probability_of_profit}%`}</dd>
                </dl>
              </div>
            ))}
          </div>
          <Assumptions a={data.assumptions} />
        </div>
      )}
    </Panel>
  );
};

export default function DemoPage() {
  return (
    <div className="min-h-screen bg-[#090d16] text-slate-200" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }} data-testid="demo-page">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950/85 border-b border-slate-800/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white" data-testid="demo-back-home"><ArrowLeft className="h-4 w-4" /> Option Wizard</Link>
          <Link to="/login" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm border border-slate-700/80" data-testid="demo-sign-in-btn"><LogIn className="h-4 w-4" /> Sign In</Link>
        </div>
      </header>
      <div className="sticky top-[57px] z-40 bg-amber-500 text-slate-950 px-4 py-2 text-center text-xs sm:text-sm font-mono font-bold tracking-wider uppercase border-b border-amber-600 shadow-md" data-testid="demo-mode-banner">{LABEL}</div>
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10 space-y-8">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.18em] text-amber-400 mb-2">Interactive demonstration</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Explore how Option Wizard analyzes positions</h1>
          <p className="text-slate-400 mt-3 max-w-3xl">Every number below is computed by the application&apos;s real calculation engines on synthetic sample data. Nothing here is a live quote, a trade signal, or premium access. No account is required.</p>
        </div>
        <PositionDemo />
        <RollmasterDemo />
        <RollCompareDemo />
        <p className="text-xs text-slate-500" data-testid="demo-footnote">{LABEL}. Options trading involves risk and is not suitable for everyone. Analytical outputs are not guarantees of future results. The premium tools use delayed market quotes and require a subscription.</p>
      </main>
      <SiteFooter />
    </div>
  );
}
